import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ClassItem } from "@/data/classes";

/**
 * Phase 1: DB-backed lifecycle & visibility.
 *
 * Every class — legacy static or database-backed — is keyed here by a stable
 * slug. This table is the ONLY source public surfaces consult for visibility.
 * Long-form content still lives where it currently lives (static file or
 * `classes` table); Phase 2 will unify content itself.
 */

export type VisibilityStatus =
  | "published"
  | "coming_soon"
  | "instructor_wanted"
  | "waitlist"
  | "hidden"
  | "draft"
  | "proposal"
  | "archived";


export interface LifecycleStatusMeta {
  key: VisibilityStatus;
  label: string;
  dot: string;               // small colored dot emoji-style class (for badges)
  badgeClass: string;        // Tailwind classes for solid badges
  publiclyVisible: boolean;  // shows on public site
  allowsEnrollment: boolean; // "Register / Enroll now" allowed
  allowsWaitlist: boolean;   // "Join Waitlist" allowed
  allowsInterest: boolean;   // "Notify me / Join interest list" allowed
  description: string;
}

/**
 * Single source of truth for every lifecycle status.
 * UI must iterate this — never hardcode a status list.
 */
export const STATUS_META: Record<VisibilityStatus, LifecycleStatusMeta> = {
  published: {
    key: "published",
    label: "Published",
    dot: "bg-emerald-500",
    badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-300",
    publiclyVisible: true,
    allowsEnrollment: true,
    allowsWaitlist: false,
    allowsInterest: false,
    description: "Visible publicly. Registration allowed.",
  },
  coming_soon: {
    key: "coming_soon",
    label: "Coming Soon",
    dot: "bg-sky-500",
    badgeClass: "bg-sky-100 text-sky-800 border-sky-300",
    publiclyVisible: true,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: true,
    description: "Visible publicly. Registration disabled. Interest list open.",
  },
  instructor_wanted: {
    key: "instructor_wanted",
    label: "Instructor Wanted",
    dot: "bg-amber-500",
    badgeClass: "bg-amber-100 text-amber-900 border-amber-300",
    publiclyVisible: true,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: false,
    description: "Recruiting an instructor. Public 'Teach This Class' CTA.",
  },
  waitlist: {
    key: "waitlist",
    label: "Waitlist",
    dot: "bg-purple-500",
    badgeClass: "bg-purple-100 text-purple-800 border-purple-300",
    publiclyVisible: true,
    allowsEnrollment: false,
    allowsWaitlist: true,
    allowsInterest: false,
    description: "Full. New families join the waitlist instead of enrolling.",
  },
  proposal: {
    key: "proposal",
    label: "Proposal",
    dot: "bg-indigo-400",
    badgeClass: "bg-indigo-100 text-indigo-800 border-indigo-300",
    publiclyVisible: false,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: false,
    description:
      "Approved Want-to-Teach proposal. Admin only — intentionally incomplete until you finish it.",
  },
  draft: {
    key: "draft",
    label: "Draft",
    dot: "bg-slate-300",
    badgeClass: "bg-slate-100 text-slate-700 border-slate-300",
    publiclyVisible: false,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: false,
    description: "Internal only. Never visible publicly.",
  },
  hidden: {

    key: "hidden",
    label: "Hidden",
    dot: "bg-neutral-500",
    badgeClass: "bg-neutral-200 text-neutral-800 border-neutral-300",
    publiclyVisible: false,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: false,
    description: "Hidden from all public pages. Still editable in Admin.",
  },
  archived: {
    key: "archived",
    label: "Archived",
    dot: "bg-red-500",
    badgeClass: "bg-red-100 text-red-800 border-red-300",
    publiclyVisible: false,
    allowsEnrollment: false,
    allowsWaitlist: false,
    allowsInterest: false,
    description: "Retired. Hidden by default in Admin.",
  },
};

/** Ordered list of statuses for menus/filters/dropdowns. */
export const LIFECYCLE_STATUSES: VisibilityStatus[] = [
  "published",
  "coming_soon",
  "instructor_wanted",
  "waitlist",
  "proposal",
  "draft",

  "hidden",
  "archived",
];

export interface RegistryEntry {
  id: string;
  slug: string;
  visibility_status: VisibilityStatus;
  registration_enabled: boolean;
  calendar_visible: boolean;
  archived_at: string | null;
  linked_class_id: string | null;
  notes: string | null;
  updated_at: string;
}

/** Strip the "db:" prefix used by ClassItem ids for database-backed classes. */
export function slugOf(input: ClassItem | string): string {
  const raw = typeof input === "string" ? input : input.id;
  return raw.startsWith("db:") ? raw.slice(3) : raw;
}

export async function fetchClassRegistry(): Promise<RegistryEntry[]> {
  const { data, error } = await (supabase as any)
    .from("class_registry")
    // Explicit columns: `notes` is internal-only and not readable by anonymous visitors.
    .select(
      "id, slug, visibility_status, registration_enabled, calendar_visible, archived_at, linked_class_id, updated_at",
    );
  if (error) {
    console.error("fetch class_registry", error);
    return [];
  }
  return (data ?? []) as RegistryEntry[];
}

export async function upsertRegistryForSlug(
  slug: string,
  patch: Partial<Pick<RegistryEntry, "visibility_status" | "registration_enabled" | "calendar_visible" | "notes">>,
): Promise<{ error: unknown }> {
  const archived_at = patch.visibility_status === "archived" ? new Date().toISOString() : null;
  const { error } = await (supabase as any)
    .from("class_registry")
    .upsert(
      {
        slug,
        ...patch,
        ...(patch.visibility_status !== undefined ? { archived_at } : {}),
      },
      { onConflict: "slug" },
    );
  if (error) return { error };

  // Keep the storage-level gate (`classes.status` / `classes.hidden`) in sync
  // with the registry, which is the visibility authority. Without this a class
  // can read "Published" in admin while Row Level Security and the public query
  // still refuse to serve it (the Music Theory class failure mode).
  if (patch.visibility_status !== undefined) {
    const { classGateForVisibility } = await import("@/lib/publicVisibility");
    const gate = classGateForVisibility(patch.visibility_status);
    if (Object.keys(gate).length > 0) {
      await (supabase as any).from("classes").update(gate).eq("slug", slug);
    }
  }
  return { error: null };
}


export interface RegistryLookup {
  entries: Map<string, RegistryEntry>;
  loading: boolean;
  isPublished: (slug: string) => boolean;
  isCalendarVisible: (slug: string) => boolean;
  isRegistrationEnabled: (slug: string) => boolean;
  statusOf: (slug: string) => VisibilityStatus;
  refresh: () => Promise<void>;
}

/**
 * Hook: live view of the class registry.
 * Unknown slugs default to `published` so a newly-added static class remains
 * visible until an admin explicitly hides it. Once a registry row exists it is
 * the sole authority.
 */
export function useClassRegistry(): RegistryLookup {
  const [entries, setEntries] = useState<Map<string, RegistryEntry>>(new Map());
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const rows = await fetchClassRegistry();
    setEntries(new Map(rows.map((r) => [r.slug, r])));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const rows = await fetchClassRegistry();
      if (!mounted) return;
      setEntries(new Map(rows.map((r) => [r.slug, r])));
      setLoading(false);
    })();
    const ch = supabase
      .channel("class-registry")
      .on("postgres_changes", { event: "*", schema: "public", table: "class_registry" }, () => {
        void refresh();
      })
      .subscribe();
    return () => {
      mounted = false;
      supabase.removeChannel(ch);
    };
  }, []);

  return useMemo<RegistryLookup>(() => {
    const get = (slug: string) => entries.get(slug);
    return {
      entries,
      loading,
      isPublished: (slug) => {
        const e = get(slug);
        return e ? STATUS_META[e.visibility_status].publiclyVisible : true;
      },
      isCalendarVisible: (slug) => {
        const e = get(slug);
        if (!e) return true;
        return STATUS_META[e.visibility_status].publiclyVisible && e.calendar_visible;
      },
      isRegistrationEnabled: (slug) => {
        const e = get(slug);
        if (!e) return true;
        return STATUS_META[e.visibility_status].allowsEnrollment && e.registration_enabled;
      },
      statusOf: (slug) => (get(slug)?.visibility_status ?? "published"),
      refresh,
    };
  }, [entries, loading]);
}
